// @custom — product-specific route overrides.
// mergeRoutes() in @system/AppRoutes replaces system routes by path.
// This file is NEVER overwritten during template sync.
//
// The / route imports @custom/LandingPage which delegates to @system by default.
// Products customise the landing page by editing
// pages/static/@custom/LandingPage/index.jsx.
//
// Add product-specific route overrides below:

import { lazy } from 'react'
import { ProtectedRoute } from '@/app/components/@system/ProtectedRoute'

// Cluboria ships its own Terms of Service page (a template for lawyer review)
// instead of the generic legal boilerplate. Imported lazily so it stays in the
// "pages-static" chunk like the other static pages it replaces.
const TermsPage = lazy(() =>
  import(/* webpackChunkName: "pages-static" */ '@/app/@custom/pages/terms').then((m) => ({
    default: m.TermsPage,
  }))
)

// Cluboria ships its own About page (who is behind the game and why) instead of the
// generic template copy. Lazy for the same reason as TermsPage: it replaces a page that
// already lives in the "pages-static" chunk.
const AboutPage = lazy(() =>
  import(/* webpackChunkName: "pages-static" */ '@/app/@custom/pages/about/page').then((m) => ({
    default: m.AboutPage,
  }))
)

// Manager Dashboard — the primary landing page after login.
// Loaded lazily in its own chunk so it doesn't bloat the initial page load.
const ManagerDashboard = lazy(() =>
  import(/* webpackChunkName: "manager" */ '@/app/@custom/pages/ManagerDashboard').then((m) => ({
    default: m.ManagerDashboard,
  }))
)

const LeagueDashboardPage = lazy(() =>
  import(/* webpackChunkName: "pages-league" */ '@/app/@custom/pages/LeagueDashboard').then((m) => ({
    default: m.LeagueDashboard,
  }))
)

const LeagueTablePage = lazy(() =>
  import(/* webpackChunkName: "pages-league" */ '@/app/@custom/pages/LeagueTable').then((m) => ({
    default: m.LeagueTable,
  }))
)

const MatchListPage = lazy(() =>
  import(/* webpackChunkName: "pages-league" */ '@/app/@custom/pages/MatchList').then((m) => ({
    default: m.MatchList,
  }))
)

// Reservation cancel/reschedule page — no auth required, the signed token in the
// URL query string authorises the operation.
const ReservationPage = lazy(() =>
  import(/* webpackChunkName: "pages-static" */ '@/app/@custom/pages/reschedule').then((m) => ({
    default: m.ReschedulePage,
  }))
)

// Reservation confirmation page — shown after a successful booking via
// the /booking/confirmed/:id route. Requires ?cancelToken= from the email.
const ConfirmationPage = lazy(() =>
  import(/* webpackChunkName: "pages-static" */ '@/app/@custom/pages/confirmation').then((m) => ({
    default: m.ConfirmationPage,
  }))
)
// Cluboria dashboard — the six club sections (Team, Squad, Tactics, Academy,
// Market, Games) live under /dashboard and are wrapped in ProtectedRoute.
const DashboardPage = lazy(() =>
  import(/* webpackChunkName: "dashboard" */ '@/app/@custom/pages/DashboardPage').then((m) => ({
    default: m.DashboardPage,
  }))
)

const TeamPage = lazy(() =>
  import(/* webpackChunkName: "dashboard" */ '@/app/@custom/pages/TeamPage').then((m) => ({
    default: m.TeamPage,
  }))
)

const SquadPage = lazy(() =>
  import(/* webpackChunkName: "dashboard" */ '@/app/@custom/pages/SquadPage').then((m) => ({
    default: m.SquadPage,
  }))
)

const TacticsPage = lazy(() =>
  import(/* webpackChunkName: "dashboard" */ '@/app/@custom/pages/TacticsPage').then((m) => ({
    default: m.TacticsPage,
  }))
)

const AcademyPage = lazy(() =>
  import(/* webpackChunkName: "dashboard" */ '@/app/@custom/pages/AcademyPage').then((m) => ({
    default: m.AcademyPage,
  }))
)

const MarketPage = lazy(() =>
  import(/* webpackChunkName: "dashboard" */ '@/app/@custom/pages/MarketPage').then((m) => ({
    default: m.MarketPage,
  }))
)

const GamesPage = lazy(() =>
  import(/* webpackChunkName: "dashboard" */ '@/app/@custom/pages/GamesPage').then((m) => ({
    default: m.GamesPage,
  }))
)

// Onboarding page — shown to new managers after signup.
// Overrides @system OnboardingPage with the club-creation wizard
// (name → type → logo → review); steps come from @/config/@custom/onboarding.
const OnboardingPage = lazy(() =>
  import(/* webpackChunkName: "pages-static" */ '@/app/@custom/pages/OnboardingPage').then((m) => ({
    default: m.OnboardingPage,
  }))
)
/** @type {import('../@system/utils').RouteDef[]} */
export const customRoutes = [
  { path: '/terms', element: <TermsPage /> },
  { path: '/about', element: <AboutPage /> },
  { path: '/manager', element: <ProtectedRoute><ManagerDashboard /></ProtectedRoute> },
  { path: '/app/leagues/:leagueId', element: <LeagueDashboardPage /> },
  { path: '/app/leagues/:leagueId/table', element: <LeagueTablePage /> },
  { path: '/app/leagues/:leagueId/matches', element: <MatchListPage /> },
  { path: '/reservation/:id/:action', element: <ReservationPage /> },
  { path: '/dashboard', element: <ProtectedRoute><DashboardPage /></ProtectedRoute> },
  { path: '/dashboard/team', element: <ProtectedRoute><TeamPage /></ProtectedRoute> },
  { path: '/dashboard/squad', element: <ProtectedRoute><SquadPage /></ProtectedRoute> },
  { path: '/dashboard/tactics', element: <ProtectedRoute><TacticsPage /></ProtectedRoute> },
  { path: '/dashboard/academy', element: <ProtectedRoute><AcademyPage /></ProtectedRoute> },
  { path: '/booking/confirmed/:id', element: <ConfirmationPage /> },
  { path: '/dashboard/market', element: <ProtectedRoute><MarketPage /></ProtectedRoute> },
  { path: '/dashboard/games', element: <ProtectedRoute><GamesPage /></ProtectedRoute> },
  { path: '/onboarding', element: <ProtectedRoute><OnboardingPage /></ProtectedRoute> },
  { path: '/dashboard', element: <ProtectedRoute><ManagerDashboard /></ProtectedRoute> },
]
